import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro } from "@/utils/preisAnalyse";

/**
 * Die Seite zur Spiegel-Fotobox.
 *
 * Der auffälligste Wert im Katalog steht gleich am Anfang: Keine einzige
 * der Spiegelboxen kommt per Versand. Wer danach sucht und eine Box nach
 * Hause geschickt bekommen will, sollte das erfahren, bevor er vergleicht.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-6 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function SpiegelInhalt({ analyse, stand }) {
  if (!analyse) return null;

  const aufschlag = analyse.standard
    ? analyse.median - analyse.standard.median
    : null;

  return (
    <div className="space-y-12 lg:space-y-16">
      <Abschnitt
        id="nur-vor-ort"
        titel="Eine Spiegel-Fotobox kommt nicht per Post"
        untertitel="Der Punkt, der die Suche am schnellsten entscheidet."
      >
        <p>
          Im Vergleich stehen {analyse.anzahlBoxen} Spiegel-Fotoboxen mit{" "}
          {analyse.anzahlPakete} Paketen —{" "}
          {analyse.perVersand === 0 ? (
            <>
              und{" "}
              <strong className="font-semibold text-slate-900">
                keine einzige davon per Versand
              </strong>
            </>
          ) : (
            <>
              davon {analyse.perVersand} per Versand
            </>
          )}
          . Alle werden vom Anbieter geliefert und aufgebaut.
        </p>
        <p className="mt-4">
          Das hat einen einfachen Grund: Ein Spiegel von{" "}
          {analyse.bildschirme.length > 1
            ? `${analyse.bildschirme[0]} bis ${analyse.bildschirme[analyse.bildschirme.length - 1]} Zoll`
            : `${analyse.bildschirme[0]} Zoll`}{" "}
          ist groß, schwer und bruchempfindlich. Er passt in kein Paket, das
          ein Paketdienst ohne Weiteres transportiert. Wer eine Fotobox nach
          Hause geschickt bekommen möchte, findet sie unter den{" "}
          <Link
            href="/fotobox-vergleich?delivery=post"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Versandboxen
          </Link>
          {" "}— dort allerdings in klassischer Bauform.
        </p>
      </Abschnitt>

      <Abschnitt
        id="preis"
        titel="Was eine Spiegel-Fotobox kostet"
        untertitel="Und wie sie sich zur klassischen Box verhält."
      >
        <p>
          Die Preise beginnen bei{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.min)} €
          </strong>
          , die Hälfte der Pakete liegt unter {euro(analyse.median)} €.
        </p>
        {analyse.standard && aufschlag !== null && (
          <p className="mt-4">
            Klassische Boxen auf dem Stativ liegen im Median bei{" "}
            {euro(analyse.standard.median)} € und beginnen bei{" "}
            {euro(analyse.standard.min)} €. Eine Spiegelbox kostet also rund{" "}
            <strong className="font-semibold text-slate-900">
              {euro(aufschlag)} € mehr
            </strong>
            . Darin steckt nicht nur das Gerät, sondern auch die Anfahrt und
            der Aufbau — beides ist bei dieser Bauform immer dabei.
          </p>
        )}
      </Abschnitt>

      <Abschnitt
        id="wann-lohnt"
        titel="Wann sich der Spiegel lohnt"
        untertitel="Und wann die klassische Box die bessere Wahl ist."
      >
        <p>
          Der Spiegel ist ein Möbelstück. Er steht mannshoch im Raum, zeigt
          Animationen auf der Glasfläche und ist von der ersten Minute an ein
          Blickfang — auch wenn niemand davorsteht. Auf einer Hochzeit im Saal
          oder bei einer Firmenfeier mit Empfang wirkt das anders als ein
          Stativ mit Tablet.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-slate-900">
          Dafür braucht er Platz und einen Boden, der trägt
        </h3>
        <p className="mt-2">
          Rechnet mit rund zwei Quadratmetern, plus Fläche davor, damit
          Gruppen hineinpassen. Und klärt den Weg zur Location: Der Spiegel
          muss durch die Tür, oft über eine Treppe, manchmal durch einen
          Hinterhof. Anbieter fragen das meist vorher ab — sagt es lieber von
          selbst.
        </p>
        <h3 className="mt-8 text-lg font-semibold text-slate-900">
          Wann die klassische Box besser passt
        </h3>
        <p className="mt-2">
          In einer Wohnung, bei einer Feier unter 40 Gästen oder wenn das
          Budget entscheidet. Die Bildqualität hängt nicht an der Bauform,
          sondern an Kamera und Licht — eine Standardbox mit
          Spiegelreflexkamera macht bessere Bilder als ein Spiegel mit
          einfacher Kamera. Was die{" "}
          <Link
            href="/#bauformen"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Bauformen sonst unterscheidet
          </Link>
          , steht im Überblick.
        </p>
        <p className="mt-6 text-xs text-slate-500">
          Alle Zahlen aus dem Katalog, Stand {stand}.
        </p>
      </Abschnitt>
    </div>
  );
}
